import { PortalQuoteResponseType } from 'stringline-types';

type Attachment = PortalQuoteResponseType['data']['attachments'][number];

interface AttachmentsProps {
  attachments: Attachment[];
}

const IMAGE_EXT = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif']);

const isImage = (att: Attachment): boolean => IMAGE_EXT.has(att.extension.toLowerCase());

const Attachments = ({ attachments }: AttachmentsProps) => {
  if (attachments.length === 0) {
    return null;
  }

  const images = attachments.filter(isImage);
  const files = attachments.filter((a) => !isImage(a));

  return (
    <section aria-label="Attachments">
      <h2 className="text-base font-semibold text-slate-900">Attachments</h2>

      {images.length > 0 ? (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {images.map((att) => (
            <a
              key={att.id}
              href={att.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block overflow-hidden rounded-md border border-slate-200 bg-slate-100"
            >
              <img
                src={att.url}
                alt={att.name}
                width={att.width ?? undefined}
                height={att.height ?? undefined}
                loading="lazy"
                className="h-32 w-full object-cover transition group-hover:opacity-90"
              />
              <span className="block truncate px-2 py-1 text-xs text-slate-600">{att.name}</span>
            </a>
          ))}
        </div>
      ) : null}

      {files.length > 0 ? (
        <ul className="mt-3 divide-y divide-slate-100 rounded-md border border-slate-200">
          {files.map((att) => (
            <li key={att.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="truncate text-slate-700">{att.name}</span>
              <a
                href={att.url}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-brand-600 hover:text-brand-700 hover:underline"
              >
                Download
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

export default Attachments;
